import plur from "plur";
import { PARAMETER_OPTIONS, PERIOD_KIND_OPTIONS } from "./constants";
import type { QueryFieldFieldsFragment } from "~/graphql/supergraphTypes";

export type DateRangeDisplay = {
  label: string;
  hasOffset: boolean;
  offsetMonths: number;
};

const PERIOD_KIND_LABEL = Object.fromEntries(
  PERIOD_KIND_OPTIONS.map((option) => [option.value, option.label]),
) as Record<(typeof PERIOD_KIND_OPTIONS)[number]["value"], string>;

const DISPLAY_DATE_LOCALE = "en-US";

const formatParameterLabel = (parameter: string | null | undefined): string | null => {
  if (!parameter) return null;
  return PARAMETER_OPTIONS.find((option) => option.value === parameter)?.label ?? parameter;
};

const formatCalendarDate = (isoDate: string | null | undefined): string | null => {
  if (!isoDate) return null;
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(DISPLAY_DATE_LOCALE, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(DISPLAY_DATE_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const deriveDateRangeDisplay = (
  datePeriod: QueryFieldFieldsFragment["datePeriod"],
): DateRangeDisplay => {
  if (!datePeriod) {
    return { label: "--", hasOffset: false, offsetMonths: 0 };
  }

  const offsetMonths = datePeriod.offsetMonths ?? 0;
  const hasOffset = offsetMonths !== 0;

  if (datePeriod.periodKind === "TRAILING_MONTHS") {
    const months = datePeriod.trailingMonths ?? 0;
    return {
      label: `Trailing ${months} ${plur("month", months)}`,
      hasOffset,
      offsetMonths,
    };
  }

  if (datePeriod.periodKind === "CUSTOM") {
    if (datePeriod.customRangeKind === "RANGE") {
      return { label: "Custom date range", hasOffset, offsetMonths };
    }

    const singleLabel =
      datePeriod.customStartParameter != null
        ? formatParameterLabel(datePeriod.customStartParameter)
        : formatCalendarDate(datePeriod.customStartDate);

    return { label: singleLabel ?? "--", hasOffset, offsetMonths };
  }

  return {
    label: PERIOD_KIND_LABEL[datePeriod.periodKind],
    hasOffset,
    offsetMonths,
  };
};
